import { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Avatar from "@mui/material/Avatar";
import Alert, { AlertColor } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";

import SignalWifi4BarIcon from "@mui/icons-material/SignalWifi4Bar";
import SignalWifiBadIcon from "@mui/icons-material/SignalWifiBad";
import SignalWifiStatusbarConnectedNoInternet4Icon from "@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4";

import { asTitle } from "utils/Utils";

import { Connection, ConnectionType, useStatus } from "status/StatusProvider";

const IMAGE_SIZE = 14;

export default function StatusIndicator(): JSX.Element {
	const { t } = useTranslation();

	const { connection } = useStatus();

	const [prevState, setPrevState] = useState<ConnectionType>();
	const [showMessage, setShowMessage] = useState(false);

	useEffect(() => {
		if (prevState === undefined || prevState === connection) {
			setPrevState(connection);
			return;
		}

		if (
			connection === Connection.DISCONNECTED ||
			prevState === Connection.DISCONNECTED
		) {
			setShowMessage(true);
		}

		setPrevState(connection);
	}, [connection]);

	function withAvatar(child: React.ReactNode): JSX.Element {
		return (
			<Tooltip title={asTitle(t(connection.toLowerCase()))}>
				<Avatar sx={{ bgcolor: "#FFFFFF", width: 20, height: 20 }}>
					{child}
				</Avatar>
			</Tooltip>
		);
	}

	function getIcon(): JSX.Element {
		switch (connection) {
			case Connection.DISCONNECTED:
				return withAvatar(
					<SignalWifiBadIcon
						sx={{ fontSize: IMAGE_SIZE, color: "error.light" }}
					/>
				);
			case Connection.UNSTABLE:
				return withAvatar(
					<SignalWifiStatusbarConnectedNoInternet4Icon
						sx={{ fontSize: IMAGE_SIZE, color: "warning.light" }}
					/>
				);
			case Connection.CONNECTED:
				return withAvatar(
					<SignalWifi4BarIcon
						sx={{ fontSize: IMAGE_SIZE, color: "success.light" }}
					/>
				);
			default:
				return <></>;
		}
	}

	function getStatusSeverity(): AlertColor {
		switch (connection) {
			case Connection.DISCONNECTED:
				return "error";
			case Connection.UNSTABLE:
			case Connection.CONNECTED:
				return "success";
			default:
				return "info";
		}
	}
	const closeMessage = useCallback((): void => {
		setShowMessage(false);
	}, []);

	return (
		<>
			{getIcon()}
			<Snackbar
				open={showMessage}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				autoHideDuration={6000}
				onClose={closeMessage}>
				<Alert onClose={closeMessage} severity={getStatusSeverity()}>
					{t(
						"connection" +
							asTitle(
								connection === Connection.DISCONNECTED
									? connection.toLowerCase()
									: Connection.CONNECTED.toLowerCase()
							)
					)}
				</Alert>
			</Snackbar>
		</>
	);
}
